/**
 * SplashScreen.js - Animated splash screen shown during auth/data initialization
 *
 * Displays a full-screen animated background with the wave gradient image,
 * providing a polished loading experience instead of flashing the login screen.
 * The animation creates a gentle swishing motion from right to left.
 */

import { Box, chakra } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { Z_INDEX } from '../ui-new/constants/layout';
import React, { useEffect, useState } from 'react';

/**
 * Keyframe animation for smooth continuous swishing wave motion.
 * Simple left-right pan with consistent scale for fluid motion.
 */
const swish = keyframes`
  0% {
    transform: scale(1.4) translateX(8%);
  }
  100% {
    transform: scale(1.4) translateX(-8%);
  }
`;

/**
 * Animated background div using chakra factory for emotion css support
 */
const AnimatedBg = chakra('div', {
  baseStyle: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundImage: "url('/splash-wave.png')",
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    willChange: 'transform',
  },
});

/**
 * Minimum time (ms) to show the splash screen for a smooth experience
 */
const MIN_DISPLAY_TIME = 1500;

/**
 * SplashScreen - Full-screen animated loading screen
 *
 * @param {{ isLoading?: boolean }} props - If isLoading is false,
 *   splash will hide after minimum display time has elapsed
 * @returns {JSX.Element|null} Animated splash screen with wave background
 *
 * Features:
 * - Full viewport coverage (100vw x 100vh)
 * - Wave image scaled to fill and overflow for smooth animation
 * - Continuous swishing motion from right to left with zoom pulse
 * - Fixed positioning to overlay everything
 * - Minimum display time to prevent jarring flash
 */
const SplashScreen = ({ isLoading = true }) => {
  const [visible, setVisible] = useState(true);
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);

  // Ensure minimum display time before allowing hide
  useEffect(() => {
    const timer = setTimeout(() => {
      setMinTimeElapsed(true);
    }, MIN_DISPLAY_TIME);
    return () => clearTimeout(timer);
  }, []);

  // Hide splash when both: loading is done AND minimum time elapsed
  useEffect(() => {
    if (!isLoading && minTimeElapsed) {
      setVisible(false);
    }
  }, [isLoading, minTimeElapsed]);

  if (!visible) {
    return null;
  }

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      width="100vw"
      height="100vh"
      overflow="hidden"
      zIndex={Z_INDEX.splash}
      bg="orange.500"
    >
      {/* Animated wave background */}
      <AnimatedBg
        sx={{
          animation: `${swish} 2s ease-in-out infinite alternate`,
        }}
      />
    </Box>
  );
};

export default SplashScreen;
